import clsx from 'clsx';
import {
  Children,
  cloneElement,
  FC,
  isValidElement,
  ReactNode,
  useRef,
  useState,
} from 'react';
import { CSSTransition } from 'react-transition-group';

import { useIsMiddleSchoolView } from 'src/hooks/use-is-middle-school-view';
import { generateUniqueID } from 'src/utils/unique-id';

import './tooltip.scss';

export type TooltipProps = {
  children: ReactNode;
  title: string;
  classNames?: {
    child?: string;
    tooltip?: string;
    container?: string;
  };
  onFocus?: () => void;
  arrowDirection?: 'left' | 'right' | 'up' | 'down';
  arrowHorizontalAlign?: 'left' | 'right';
  appearDelay?: number;
  suppress?: boolean;
};

/**
 * Positioning should be done via classNames.tooltip
 * */
export const Tooltip: FC<TooltipProps> = ({
  children,
  title,
  classNames,
  onFocus,
  arrowDirection,
  arrowHorizontalAlign,
  appearDelay,
  suppress,
}) => {
  const tooltipId = generateUniqueID();

  const tooltipRef = useRef<HTMLParagraphElement>(null);

  const [isVisible, setIsVisible] = useState(false);

  const isMiddleSchoolView = useIsMiddleSchoolView();

  const showTooltip = () => {
    if (suppress) return;

    setIsVisible(true);
  };
  const hideTooltip = () => setIsVisible(false);

  const handleFocus = () => {
    if (suppress) return;

    showTooltip();
    onFocus?.();
  };

  const getChildElement = () => {
    const child = Children.toArray(children)[0];

    if (!isValidElement(child)) return;

    const childClone = cloneElement(child as React.ReactElement, {
      onMouseOver: showTooltip,
      onMouseLeave: hideTooltip,
      onFocus: showTooltip,
      onBlur: hideTooltip,
    });

    if (classNames?.child)
      return <div className={classNames.child}>{childClone}</div>;

    return childClone;
  };

  const getTooltipAnimationDuration = () => {
    if (isMiddleSchoolView) {
      return appearDelay ?? 1000;
    }

    return appearDelay ?? 300;
  };

  return (
    <div
      className={clsx('tooltip-box', classNames?.container)}
      onMouseOver={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={handleFocus}
      onBlur={hideTooltip}
      aria-describedby={tooltipId}
    >
      {getChildElement()}
      <CSSTransition
        nodeRef={tooltipRef}
        in={isVisible}
        timeout={getTooltipAnimationDuration()}
        classNames={isMiddleSchoolView ? 'tooltip-fade' : 'fade'}
        unmountOnExit
      >
        <p
          id={tooltipId}
          ref={tooltipRef}
          className={clsx(
            'tooltip-box__message',
            {
              'tooltip-box__message--middle-school': isMiddleSchoolView,
              [`arrow-${arrowDirection}`]:
                isMiddleSchoolView && !!arrowDirection,
              [`arrow-align-${arrowHorizontalAlign}`]: arrowHorizontalAlign,
            },
            classNames?.tooltip,
          )}
          role="tooltip"
          data-testid="tooltip"
        >
          {title}
        </p>
      </CSSTransition>
    </div>
  );
};
